import { create } from 'zustand';
import { authService } from '@/services/auth.service';
import type { AuthUser } from '@/services/auth.service';

const USER_KEY = 'bbpos_kds_user';
const STATION_KEY = 'bbpos_kds_station';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrated: boolean;
  selectedStationId: string | null;

  verifyOtp: (email: string, otp: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hydrate: () => void;
  setStation: (stationId: string | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isHydrated: false,
  selectedStationId: null,

  verifyOtp: async (email, otp) => {
    const data = await authService.verifyOtp(email, otp);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    set({ user: data.user, isAuthenticated: true });
  },

  login: async (email, password) => {
    const data = await authService.login(email, password);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    set({ user: data.user, isAuthenticated: true });
  },

  logout: async () => {
    try {
      await authService.logout();
    } catch {
      // Ignore — clear local state regardless
    }
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(STATION_KEY);
    set({
      user: null,
      isAuthenticated: false,
      selectedStationId: null,
    });
  },

  hydrate: () => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      const station = localStorage.getItem(STATION_KEY);
      if (raw) {
        const user = JSON.parse(raw) as AuthUser;
        set({
          user,
          isAuthenticated: true,
          isHydrated: true,
          selectedStationId: station ?? null,
        });
      } else {
        set({ isHydrated: true });
      }
    } catch {
      localStorage.removeItem(USER_KEY);
      set({ isHydrated: true });
    }
  },

  setStation: (stationId) => {
    if (stationId) {
      localStorage.setItem(STATION_KEY, stationId);
    } else {
      localStorage.removeItem(STATION_KEY);
    }
    set({ selectedStationId: stationId });
  },
}));
